import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, Zap, Trash2, Users, Megaphone, Send, CalendarClock, GraduationCap, Wrench } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../lib/api";
import SignalCard from "../components/SignalCard";
import { MtPanel } from "../components/MtPanel";
import { LotteryAdmin } from "../components/LotteryAdmin";
import { ChampionshipAdmin } from "../components/ChampionshipAdmin";
import { logError } from "../lib/logger";

const EMPTY = {
  symbol: "", asset_class: "gold", direction: "BUY", entry: "", stop_loss: "",
  take_profit_1: "", take_profit_2: "", timeframe: "M30", commentary: "", is_premium: false,
};

const TF_OPTIONS = ["M15", "M30", "H2", "H4", "H8", "D1", "Weekly"];

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-1.5">{label}</label>
    {children}
  </div>
);

const inputCls = "w-full px-3 py-2 rounded-md bg-[#0A0A0A] border border-white/10 text-white text-sm focus:border-blue-500 focus:outline-none transition-colors";

export default function Admin() {
  const [signals, setSignals] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ title: "", body: "" });
  const [sendingMsg, setSendingMsg] = useState(false);
  const [ss, setSs] = useState({ seq: "", status: "sl_hit" });
  const [ssBusy, setSsBusy] = useState(false);

  const setSignalStatus = async () => {
    if (!String(ss.seq).trim()) { toast.error("Inserisci l'ID del segnale"); return; }
    setSsBusy(true);
    try {
      const { data } = await api.post(`/admin/signals/${ss.seq}/set-status`, { status: ss.status });
      toast.success(`ID ${data.seq} → ${data.status.toUpperCase()}`);
      setSs({ seq: "", status: "sl_hit" });
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSsBusy(false); }
  };

  const load = useCallback(async () => {
    try { const { data } = await api.get("/signals"); setSignals(data); }
    catch (e) { logError("Errore caricamento segnali:", e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        entry: parseFloat(form.entry),
        stop_loss: parseFloat(form.stop_loss),
        take_profit_1: parseFloat(form.take_profit_1),
        take_profit_2: form.take_profit_2 ? parseFloat(form.take_profit_2) : null,
      };
      await api.post("/signals", payload);
      toast.success("Segnale pubblicato e notifiche inviate!");
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const update = async (id, body) => {
    try { await api.put(`/signals/${id}`, body); toast.success("Segnale aggiornato"); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/signals/${id}`); toast.success("Segnale eliminato"); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const scanNow = async () => {
    try {
      const { data } = await api.post("/signals/scan");
      toast.success(data.message || "Scansione avviata");
      setTimeout(load, 20000);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const sendBroadcast = async (e) => {
    e.preventDefault();
    if (!msg.title.trim() || !msg.body.trim()) { toast.error("Inserisci titolo e messaggio"); return; }
    setSendingMsg(true);
    try {
      await api.post("/broadcasts", { title: msg.title.trim(), body: msg.body.trim() });
      toast.success("Messaggio inviato a tutti gli iscritti (notifica push + card)!");
      setMsg({ title: "", body: "" });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setSendingMsg(false); }
  };

  const cleanupDemo = async () => {
    if (!window.confirm("Eliminare i segnali di test/fuori mercato (prezzo di entrata molto lontano da quello attuale)?")) return;
    try {
      const { data } = await api.post("/signals/cleanup-demo");
      toast.success(`Eliminati ${data.deleted} segnali di test/fuori mercato`);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-1">Pannello Admin</h1>
          <p className="text-sm text-zinc-400">Pubblica manualmente o genera segnali automatici (Shadow Liquidity Pool).</p>
        </div>
        <div className="flex flex-wrap gap-3">
        <Link data-testid="admin-network-link" to="/admin/network"
          className="flex items-center gap-2 px-4 py-2 rounded-md bg-white/5 border border-white/10 text-zinc-300 text-sm font-medium hover:bg-blue-500/15 hover:text-blue-400 hover:border-blue-500/30 transition-colors">
          <Users size={16} /> Gestione Network
        </Link>
        <Link data-testid="admin-nfp-link" to="/admin/nfp"
          className="flex items-center gap-2 px-4 py-2 rounded-md bg-white/5 border border-white/10 text-zinc-300 text-sm font-medium hover:bg-emerald-500/15 hover:text-emerald-400 hover:border-emerald-500/30 transition-colors">
          <CalendarClock size={16} /> NFP · Dati reali
        </Link>
        <Link data-testid="admin-learning-link" to="/admin/learning"
          className="flex items-center gap-2 px-4 py-2 rounded-md bg-white/5 border border-white/10 text-zinc-300 text-sm font-medium hover:bg-purple-500/15 hover:text-purple-400 hover:border-purple-500/30 transition-colors">
          <GraduationCap size={16} /> Learning
        </Link>
        <button data-testid="scan-now-btn" onClick={scanNow}
          className="flex items-center gap-2 px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-500 transition-colors">
          <Zap size={16} /> Scansiona mercati ora
        </button>
        <button data-testid="cleanup-demo-btn" onClick={cleanupDemo}
          className="flex items-center gap-2 px-4 py-2 rounded-md bg-white/5 border border-white/10 text-zinc-300 text-sm font-medium hover:bg-red-500/15 hover:text-red-400 hover:border-red-500/30 transition-colors">
          <Trash2 size={16} /> Pulisci segnali test/fuori mercato
        </button>
        </div>
      </div>

      <div data-testid="force-status-tool" className="mb-8 bg-[#141414] border border-amber-500/25 rounded-md p-5">
        <div className="flex items-center gap-2 mb-1">
          <Wrench size={16} className="text-amber-400" />
          <h2 className="text-base font-semibold text-white">Forza stato segnale</h2>
        </div>
        <p className="text-xs text-zinc-500 mb-4">Imposta manualmente lo stato di un segnale tramite il suo ID (es. 100601). Allinea anche MT5 e invia la notifica.</p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-1.5">ID segnale</label>
            <input data-testid="force-status-seq" type="number" value={ss.seq}
              onChange={(e) => setSs((v) => ({ ...v, seq: e.target.value }))}
              placeholder="100601" className={`${inputCls} w-40`} />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-1.5">Nuovo stato</label>
            <select data-testid="force-status-select" value={ss.status}
              onChange={(e) => setSs((v) => ({ ...v, status: e.target.value }))}
              className={`${inputCls} w-48`}>
              <option value="sl_hit">STOP LOSS</option>
              <option value="tp_hit">TAKE PROFIT</option>
              <option value="be_hit">BREAKEVEN</option>
              <option value="cancelled">CANCELLATO</option>
              <option value="active">ATTIVO (ripristina)</option>
            </select>
          </div>
          <button data-testid="force-status-btn" onClick={setSignalStatus} disabled={ssBusy}
            className="flex items-center gap-2 px-5 py-2 rounded-md bg-amber-600 text-white text-sm font-medium hover:bg-amber-500 transition-colors disabled:opacity-50">
            <Wrench size={15} /> {ssBusy ? "Applico…" : "Applica stato"}
          </button>
        </div>
      </div>

      <MtPanel />

      <LotteryAdmin />

      <ChampionshipAdmin />

      <div className="bg-[#141414] border border-blue-500/30 rounded-md p-6 mb-6">
        <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
          <Megaphone size={18} className="text-blue-400" /> Messaggio a tutti gli iscritti
        </h2>
        <p className="text-xs text-zinc-500 mb-4">Invia un messaggio: tutti lo ricevono come notifica push e appare come card tra i segnali (filtro "Messaggi").</p>
        <form onSubmit={sendBroadcast} className="space-y-3">
          <input data-testid="broadcast-title" value={msg.title} onChange={(e) => setMsg((m) => ({ ...m, title: e.target.value }))}
            placeholder="Titolo del messaggio" className={inputCls} />
          <textarea data-testid="broadcast-body" rows={3} value={msg.body} onChange={(e) => setMsg((m) => ({ ...m, body: e.target.value }))}
            placeholder="Scrivi qui il tuo messaggio..." className={inputCls} />
          <button data-testid="broadcast-send-btn" type="submit" disabled={sendingMsg}
            className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-500 transition-colors disabled:opacity-50">
            <Send size={16} /> {sendingMsg ? "Invio in corso..." : "Invia a tutti"}
          </button>
        </form>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="bg-[#141414] border border-white/10 rounded-md p-6 sticky top-24">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2"><Plus size={18} className="text-blue-500" /> Nuovo Segnale</h2>
            <form onSubmit={submit} className="space-y-4">
              <Field label="Simbolo">
                <input data-testid="admin-symbol" required value={form.symbol} onChange={(e) => set("symbol", e.target.value.toUpperCase())} placeholder="XAUUSD" className={`${inputCls} font-mono`} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Asset">
                  <select data-testid="admin-asset" value={form.asset_class} onChange={(e) => set("asset_class", e.target.value)} className={inputCls}>
                    <option value="gold">Oro</option>
                    <option value="crypto">Crypto</option>
                    <option value="forex">Forex</option>
                  </select>
                </Field>
                <Field label="Direzione">
                  <select data-testid="admin-direction" value={form.direction} onChange={(e) => set("direction", e.target.value)} className={inputCls}>
                    <option value="BUY">BUY</option>
                    <option value="SELL">SELL</option>
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Entry"><input data-testid="admin-entry" required type="number" step="any" value={form.entry} onChange={(e) => set("entry", e.target.value)} className={`${inputCls} font-mono`} /></Field>
                <Field label="Timeframe">
                  <select data-testid="admin-timeframe" value={form.timeframe} onChange={(e) => set("timeframe", e.target.value)} className={`${inputCls} font-mono`}>
                    {TF_OPTIONS.map((tf) => <option key={tf} value={tf}>{tf}</option>)}
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Stop Loss"><input data-testid="admin-sl" required type="number" step="any" value={form.stop_loss} onChange={(e) => set("stop_loss", e.target.value)} className={`${inputCls} font-mono`} /></Field>
                <Field label="Take Profit 1"><input data-testid="admin-tp1" required type="number" step="any" value={form.take_profit_1} onChange={(e) => set("take_profit_1", e.target.value)} className={`${inputCls} font-mono`} /></Field>
              </div>
              <Field label="Take Profit 2 (opzionale)"><input data-testid="admin-tp2" type="number" step="any" value={form.take_profit_2} onChange={(e) => set("take_profit_2", e.target.value)} className={`${inputCls} font-mono`} /></Field>
              <Field label="Commento"><textarea data-testid="admin-commentary" rows={2} value={form.commentary} onChange={(e) => set("commentary", e.target.value)} className={inputCls} /></Field>
              <label className="flex items-center gap-2 cursor-pointer">
                <input data-testid="admin-premium" type="checkbox" checked={form.is_premium} onChange={(e) => set("is_premium", e.target.checked)} className="w-4 h-4 accent-amber-500" />
                <span className="text-sm text-zinc-300">Segnale Premium</span>
              </label>
              <button data-testid="admin-submit-btn" type="submit" disabled={saving}
                className="w-full px-4 py-2.5 rounded-md bg-blue-600 text-white font-medium hover:bg-blue-500 transition-colors disabled:opacity-50">
                {saving ? "Pubblicazione..." : "Pubblica Segnale"}
              </button>
            </form>
          </div>
        </div>

        <div className="lg:col-span-2">
          <h2 className="text-lg font-semibold mb-4">Segnali Pubblicati ({signals.length})</h2>
          {signals.length === 0 ? (
            <div className="text-center py-20 text-zinc-500 border border-dashed border-white/10 rounded-md">Nessun segnale pubblicato.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {signals.map((s) => <SignalCard key={s.id} s={s} admin onUpdate={update} onDelete={remove} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
